import SharedDialog from '../../../../shared/shared-dialog';
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getUserPermission,
  moveDocuments,
  UserKeys,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import {
  ApiException,
  Doc,
  Folder,
  SharedDriveModel,
} from '../../../../../types';
import { Dispatch, SetStateAction, useState } from 'react';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import ConfirmMoveDocument from '../../../../shared/confirm-move-doc-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import axios from 'axios';
import SharedDrivesPermissionCheckDialog from './shared-drives/shared-drives-parent/shared-drives-permission-check-dialog';
import { CallbackDoc } from 'react-google-drive-picker/dist/typeDefs';
import { useUserSettings } from '../../../../utils/use-user-settings';
import DriveListDialogContent from './drive-list-dialog';

import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

interface DriveMoveDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDocs: Doc[];
  setSelectedDocs: Dispatch<SetStateAction<string[]>>;
  driveId: string;
  changeLogRoles: string[];
  isAllUsers?: boolean;
  allFolders: InfiniteData<Folder[], unknown> | undefined;
}

export default function DriveMoveDialog({
  open,
  handleClose,
  userEmail,
  selectedDocs,
  driveId,
  setSelectedDocs,
  changeLogRoles,
  isAllUsers,
  allFolders,
}: DriveMoveDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [openConfirmMoveDocDialog, setOpenConfirmMoveDocDialog] =
    useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [folder, setFolder] = useState(driveId);
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openPermissionCheckDialog, setOpenPermissionCheckDialog] =
    useState(false);
  const [selectedTab, setSelectedTab] = useState('drive');
  const [permissionCheckLoading, setPermissionCheckLoading] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState('');
  const [selectedDrivePickerFolder, setSelectedDrivePickerFolder] =
    useState<CallbackDoc | null>(null);

  const isSharedDriveFolder = selectedTab === 'sharedDrives';
  const hasFolder = selectedDocs.some((doc) => doc.mimeType.includes('folder'));

  const [selectedDrive, setSelectedDrive] = useState<SharedDriveModel | null>(
    null
  );
  const { loggedInUser } = useUserSettings();
  const { mutate: mutate, isPending: isMoving } = useMutation({
    mutationFn: (driveOrFolderId: string) =>
      moveDocuments(
        userEmail,
        selectedDocs,
        folder,
        changeLogMessage,
        isSharedDriveFolder,
        driveOrFolderId
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${selectedDocs.length} items successfully moved to ${selectedFolderTitle}.`,
      });
      void queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments],
      });
      void queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveFolders],
      });
      void queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDrivesForDomain],
      });
      void queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDrivesForUser],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDriveFolders],
      });
      void queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDriveDocuments],
      });

      setChangeLogMessage('');
      setSelectedDocs([]);
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        handleErrors({
          error,
          snackbarMessage:
            errorResponse.message ||
            'There was an issue moving the selected documents or folders. Please refresh and try again.',
        });
      }
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.move_drive_content, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.move_drive_content,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  const flattenedFolders = allFolders?.pages.reduce(
    (acc, page) => acc.concat(page),
    []
  );
  const selectedFolderDetails = flattenedFolders?.find((f) => f.id === folder);

  const selectedFolderTitle = (() => {
    if (isSharedDriveFolder) {
      return (
        selectedDrivePickerFolder?.name ||
        selectedDrive?.name ||
        'the selected folder'
      );
    }
    return folder === driveId
      ? 'My Drive'
      : selectedFolderDetails?.title || 'the selected folder';
  })();

  const selectedPickerFolderIsInDocs = selectedDocs.some(
    (doc) => doc.id === selectedDrivePickerFolder?.id
  );

  async function handleSubmit() {
    if (isSharedDriveFolder) {
      setPermissionCheckLoading(true);
      const isSharedDrive = !selectedDrivePickerFolder;

      const hasPermission = await queryClient.fetchQuery({
        queryKey: [
          UserKeys.SharedDriveUserPermission,
          loggedInUser,
          selectedDrive?.id,
        ],
        queryFn: () =>
          getUserPermission(
            selectedDrive?.id || selectedDrivePickerFolder?.id || '',
            loggedInUser,
            isSharedDrive
          ),
      });

      const allowedPermissions = ['fileOrganizer', 'organizer', 'owner'];
      const hasCorrectPermission =
        hasPermission && allowedPermissions.includes(hasPermission);

      setPermissionCheckLoading(false);

      if (hasCorrectPermission) {
        if (
          isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanMoveDocs.value)
        ) {
          setOpenChangeLogDialog(true);
        } else {
          setOpenConfirmMoveDocDialog(true);
        }
      } else {
        setOpenPermissionCheckDialog(true);
      }
    } else {
      if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanMoveDocs.value)) {
        setOpenChangeLogDialog(true);
      } else {
        setOpenConfirmMoveDocDialog(true);
      }
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={selectedDocs.length === 1 ? 'Move Document' : 'Move Documents'}
      actingAs={selectedTab === 'drive' ? userEmail : loggedInUser}
      dialogContent={
        <Box sx={{ minHeight: 250 }}>
          {selectedDocs.length === 1 ? (
            <Typography>
              Select the folder you'd like to move{' '}
              <strong>{selectedDocs[0].title}</strong> to.
            </Typography>
          ) : (
            <Typography
              sx={{
                mt: 1,
              }}
            >
              Select the folder you'd like to move the{' '}
              <strong>{selectedDocs.length} selected items</strong> to.
            </Typography>
          )}
          <Tabs
            value={selectedTab}
            onChange={(_event, newValue) => setSelectedTab(newValue)}
            sx={{
              '& .MuiTabs-flexContainer': {
                marginTop: '6px',
              },
              '.MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
          >
            <Tab
              value="drive"
              label="My Drive"
              sx={{
                minHeight: '40px',
                height: '40px',
                width: 'fit-content',
              }}
            />
            {hasFolder ? (
              <Tooltip title="Folders cannot be moved to a Shared Drive.">
                <div>
                  <Tab
                    value="sharedDrives"
                    label="Shared Drive"
                    disabled
                    sx={{
                      minHeight: '40px',
                      height: '40px',
                      width: 'fit-content',
                    }}
                  />
                </div>
              </Tooltip>
            ) : (
              <Tab
                value="sharedDrives"
                label="Shared Drive"
                sx={{
                  minHeight: '40px',
                  height: '40px',
                  width: 'fit-content',
                }}
              />
            )}
          </Tabs>
          <DriveListDialogContent
            selectedTab={selectedTab}
            userEmail={userEmail}
            driveId={driveId}
            folder={folder}
            setFolder={setFolder}
            selectedDocs={selectedDocs}
            autocompleteValue={autocompleteValue}
            setAutocompleteValue={setAutocompleteValue}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            selectedDrivePickerFolder={selectedDrivePickerFolder}
            setSelectedDrivePickerFolder={setSelectedDrivePickerFolder}
            selectedPickerFolderIsInDocs={selectedPickerFolderIsInDocs}
            openChangeLogDialog={openChangeLogDialog}
            setOpenChangeLogDialog={setOpenChangeLogDialog}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            isMoving={isMoving}
            openPermissionCheckDialog={openPermissionCheckDialog}
            setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
            changeLogRoles={changeLogRoles}
            setPermissionCheckLoading={setPermissionCheckLoading}
          />

          <ConfirmMoveDocument
            open={openConfirmMoveDocDialog}
            openChangeLogDialog={openChangeLogDialog}
            setOpenChangeLogDialog={setOpenChangeLogDialog}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            handleClose={() => setOpenConfirmMoveDocDialog(false)}
            selectedFolder={selectedFolderTitle}
            mutate={() => {
              const destinationId = isSharedDriveFolder
                ? selectedDrivePickerFolder?.id || selectedDrive?.id || ''
                : folder;

              mutate(destinationId);
            }}
            mutateLoading={isMoving}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const destinationId = isSharedDriveFolder
                ? selectedDrivePickerFolder?.id || selectedDrive?.id || ''
                : folder;
              mutate(destinationId);
            }}
            mutateLoading={isMoving}
          />
          {selectedDrive && openPermissionCheckDialog ? (
            <SharedDrivesPermissionCheckDialog
              open={openPermissionCheckDialog}
              handleClose={() => {
                setOpenPermissionCheckDialog(false);
              }}
              minimumRequiredRole="fileOrganizer"
              folderOrDriveId={selectedDrive.id}
              actionAfterPermissionGranted={() => {
                setFolder(selectedDrive.id);
              }}
              nameOfSharedDrive={selectedDrive.name}
            />
          ) : null}
        </Box>
      }
      primaryButton={{
        title: selectedDocs.length === 1 ? 'Move Document' : 'Move Documents',
        action: handleSubmit,
        loading: isMoving || permissionCheckLoading,
        disabled:
          isMoving ||
          permissionCheckLoading ||
          (isSharedDriveFolder && !selectedDrive && !selectedDrivePickerFolder),
      }}
    />
  );
}
